/**
 * ספקים — who makes the goods.
 *
 * The `Suppliers` table has existed since the product hub (doc 1 §35) but never had a screen: the
 * quote drawer built its picker out of the quotes already on the project, so a supplier who had
 * never quoted *this* project could not be reached, and the first quote of a new supplier had to
 * be entered outside the app. These two shapes are what closed that — a list for the picker and a
 * create body for the "+ ספק חדש" panel beside it.
 *
 * **`country` is free text and not an ISO-2 code.** The column was `STRING(2)` and the one seeded
 * row says `IL`, but nothing in the app maps a code to a name, so the picker would have shown
 * "CN" to a Hebrew reader. A country list that cannot express a supplier's country blocks the
 * person filling the form, and this field is read, never computed on.
 *
 * **`supplierNumber` is the ERP number** (Priority `מס_ספק`). It is unique when present and empty
 * on a supplier typed in by hand. It is not the unused `code` column.
 *
 * **`supplierType` is חו"ל / ארץ**, stored as `foreign` / `domestic`. Currency is a `PhCurrency`
 * code (`ILS`, `USD`, …), the same codes the rest of the product uses. Neither is required.
 *
 * No React, no MUI, no browser globals.
 */
import { phOptions, type PhCurrency } from './productHub.core';

export const SUPPLIER_TYPES = ['foreign', 'domestic'] as const;
export type SupplierType = (typeof SUPPLIER_TYPES)[number];

export const SUPPLIER_TYPE_LABELS: Record<SupplierType, string> = {
  foreign: 'חו"ל',
  domestic: 'ארץ'
};

export const SUPPLIER_TYPE_OPTIONS = phOptions(SUPPLIER_TYPES, SUPPLIER_TYPE_LABELS);

/** A supplier as the ספקים screen lists one. Every column the forms write comes back. */
export interface SupplierDto {
  id: number;
  name: string;
  /** Priority supplier number. Null on a supplier created by hand. Unique when set. */
  supplierNumber: string | null;
  /** `foreign` is חו"ל, `domestic` is ארץ. Null until somebody classifies the supplier. */
  supplierType: SupplierType | null;
  /** The supplier's currency, a `PhCurrency` code. Null until known. */
  currency: PhCurrency | null;
  /** ח.פ., the registered name, an address — whatever identifies the company on paper. */
  companyDetails: string | null;
  country: string | null;
  contactName: string | null;
  phone: string | null;
  email: string | null;
  notes: string | null;
  /** The registered name as it appears on an invoice — often not the name the team uses. */
  legalName: string | null;
  /** ח.פ. / עוסק מורשה / a foreign tax id. Free text: a Chinese factory's id is not nine digits. */
  taxId: string | null;
  address: string | null;
  website: string | null;
  /** One of `SUPPLIER_PAYMENT_TERMS` (`supplierInvoice.types.ts`); suggests a due date, nothing more. */
  paymentTerms: string | null;
  financeNotes: string | null;
  /** `false` hides the supplier from every picker without deleting its history. */
  active: boolean;
  /** The supplier's types — a supplier may be both a manufacturer and a packaging plant. */
  typeIds: number[];
  typeNames: string[];
  /**
   * How many production orders name this supplier. The ספקים screen reads it to decide whether
   * "מחיקה" is offered: a supplier with orders cannot be removed (the server refuses it too).
   */
  productionOrderCount: number;
}

/**
 * One row of the quote / RFQ typeahead (`GET /planning/suppliers/suggest`).
 *
 * Deliberately not the full card: the picker prints the name, the number and חו"ל/ארץ, and the
 * RFQ drawer reads `email` to warn when the message would have nowhere to go.
 */
export interface SupplierSuggestionDto {
  id: number;
  name: string;
  supplierNumber: string | null;
  /** `foreign` is חו"ל, `domestic` is ארץ. Null until somebody classifies the supplier. */
  supplierType: SupplierType | null;
  email: string | null;
}

/**
 * The body of both the "+ ספק חדש" panel and the ספקים screen's edit dialog. Only the name is
 * required — the rest arrives over time.
 */
export interface UpsertSupplierBody {
  name: string;
  supplierNumber?: string | null;
  supplierType?: SupplierType | null;
  currency?: PhCurrency | null;
  companyDetails?: string | null;
  country?: string | null;
  contactName?: string | null;
  phone?: string | null;
  email?: string | null;
  notes?: string | null;
  legalName?: string | null;
  taxId?: string | null;
  address?: string | null;
  website?: string | null;
  paymentTerms?: string | null;
  financeNotes?: string | null;
  active?: boolean;
  /** Replaces the supplier's types when present; omitted leaves them alone. */
  typeIds?: number[];
}
